import { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { ShieldCheck, FileText, LayoutList, Users, UserCircle, LogOut, Bike, Contact, Bell, Search, Menu, X, Car, Briefcase, ClipboardList, Hammer, ListTodo, ClipboardCheck, Hourglass, Settings, Package, CalendarClock, BookOpen, Languages, LayoutDashboard, History, Truck, ShieldAlert, Gauge, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BusquedaCtx } from "@/lib/busquedaHeader";

const SECCIONES = [
  {
    titulo: "INICIO", items: [
      { to: "/", label: "Tablero", icon: LayoutDashboard, testid: "nav-dashboard" },
    ],
  },
  {
    titulo: "TALLER", items: [
      { clave: "trabajos.ingresar", to: "/trabajos/ingresar", label: "Ingresar trabajo", icon: Hammer, testid: "nav-trabajo-ingresar" },
      { clave: "trabajos.ver", to: "/trabajos/activos", label: "Trabajos activos", icon: ListTodo, testid: "nav-trabajos-activos" },
      { clave: "pedidos.ver", to: "/pedidos-especiales", label: "Pedidos especiales", icon: Truck, testid: "nav-pedidos-especiales" },
      { clave: "reportes.ver", to: "/reportes/realizados", label: "Trabajos realizados", icon: ClipboardCheck, testid: "nav-trabajos-realizados" },
      { clave: "boletas.ver", to: "/reportes/pendientes", label: "Boletas de taller", icon: Hourglass, testid: "nav-boletas-taller" },
      { clave: "reportes.ver", to: "/reportes/vehiculos", label: "Historial de vehículo", icon: History, testid: "nav-vehiculo-historial" },
      { adminOnly: true, to: "/reportes/control-honda", label: "Control órdenes Honda", icon: ShieldAlert, testid: "nav-control-honda" },
      { adminOnly: true, to: "/reportes/mecanicos", label: "Trabajos por mecánico", icon: Wrench, testid: "nav-reporte-mecanicos" },
    ],
  },
  {
    titulo: "CLIENTES", items: [
      { clave: "clientes.ver", to: "/clientes/gestion", label: "Clientes", icon: Contact, testid: "nav-clientes" },
      { clave: "vehiculos.ver", to: "/vehiculos/gestion", label: "Vehículos", icon: Car, testid: "nav-vehiculos" },
      { clave: "cotizaciones.crear", to: "/cotizaciones", label: "Cotizaciones", icon: FileText, testid: "nav-cotizaciones" },
    ],
  },
  {
    titulo: "CATÁLOGO TÉCNICO", items: [
      { clave: "mant.servicios", to: "/servicios", label: "Servicios", icon: ClipboardList, testid: "nav-servicios" },
      { clave: "mant.plantillas", to: "/plantillas", label: "Plantillas", icon: LayoutList, testid: "nav-plantillas" },
      { clave: "mant.cronogramas", to: "/cronogramas", label: "Cronogramas", icon: CalendarClock, testid: "nav-cronogramas" },
      { clave: "mant.repuestos", to: "/catalogo", label: "Repuestos", icon: Package, testid: "nav-repuestos-mant" },
      { clave: "mant.catref", to: "/catalogo-referencia", label: "Catálogo de referencia", icon: BookOpen, testid: "nav-catalogo-referencia" },
      { clave: "mant.modelos", to: "/modelos", label: "Modelos", icon: Bike, testid: "nav-modelos" },
      { clave: "mant.sinonimos", to: "/sinonimos", label: "Sinónimos", icon: Languages, testid: "nav-sinonimos" },
    ],
  },
  {
    titulo: "ADMINISTRACIÓN", items: [
      { clave: "mant.empleados", to: "/empleados", label: "Empleados", icon: Briefcase, testid: "nav-empleados" },
      { clave: "mant.usuarios", to: "/usuarios", label: "Usuarios", icon: Users, testid: "nav-usuarios" },
      { clave: "mant.roles", to: "/roles", label: "Roles", icon: ShieldCheck, testid: "nav-roles" },
      { clave: "mant.config", to: "/config-taller", label: "Datos del taller", icon: Settings, testid: "nav-config-taller" },
      { adminOnly: true, to: "/uso-ia", label: "Uso de IA", icon: Gauge, testid: "nav-uso-ia" },
    ],
  },
];

const tiempoRelativo = (iso) => {
  const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} día${d > 1 ? "s" : ""}`;
};

export default function Layout({ children }) {
  const { user, logout, isAdmin, tienePermiso } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [busqCfg, setBusqCfgState] = useState(null);
  const setBusqCfg = useCallback((cfg) => { setBusqCfgState(cfg); setBusqueda(""); }, []);

  useEffect(() => { if (!busqCfg) setBusqueda(""); }, [busqCfg]);

  const cargarNotifs = useCallback(async () => {
    try { const r = await api.get("/notificaciones"); setNotifs(r.data); } catch { /* sesión expirada */ }
  }, []);

  useEffect(() => {
    cargarNotifs();
    const i = setInterval(cargarNotifs, 30000);
    return () => clearInterval(i);
  }, [cargarNotifs]);

  const buscarGlobal = (e) => {
    e.preventDefault();
    if (busqCfg?.live) return;
    const term = busqueda.trim();
    if (!term) return;
    if (busqCfg) {
      busqCfg.buscar(term);
    } else {
      navigate(`/catalogo?q=${encodeURIComponent(term)}`);
    }
    setBusqueda("");
    setSidebarOpen(false);
  };

  const descartar = async (e, n) => {
    e.stopPropagation();
    try { await api.post(`/notificaciones/${n.id}/descartar`); cargarNotifs(); } catch { }
  };

  const noLeidas = notifs.filter((n) => !n.leida).length;

  const abrirNotif = async (n) => {
    setNotifOpen(false);
    try { if (!n.leida) await api.post(`/notificaciones/${n.id}/marcar-leida`); } catch { }
    const destino = n.link || (n.trabajo_id ? `/trabajos/${n.trabajo_id}/aprobacion` : null);
    if (destino) navigate(destino);
    cargarNotifs();
  };

  const marcarTodas = async () => {
    try { await api.post("/notificaciones/marcar-todas-leidas"); cargarNotifs(); } catch { }
  };

  return (
    <BusquedaCtx.Provider value={{ query: busqueda, setQuery: setBusqueda, setConfig: setBusqCfg }}>
    <div className="min-h-screen bg-slate-100">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden no-print" onClick={() => setSidebarOpen(false)} />
      )}
      <aside data-testid="sidebar"
        className={`no-print fixed inset-y-0 left-0 z-50 w-60 bg-white border-r border-slate-200 flex flex-col transform transition-transform md:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-14 flex items-center gap-2 px-4 border-b border-slate-100 shrink-0">
          <div className="bg-primary text-white p-1.5 rounded-md"><Bike size={18} /></div>
          <span className="font-heading font-bold text-lg text-slate-800">Nextep Taller</span>
          <button className="ml-auto md:hidden text-slate-500" onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-cerrar-btn"><X size={18} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
          {SECCIONES.map((s) => ({
            ...s,
            items: s.items.filter((l) => (!l.clave || tienePermiso(l.clave)) && (!l.adminOnly || isAdmin)),
          })).filter((s) => s.items.length > 0).map((s) => (
            <div key={s.titulo}>
              <p className="px-2 mb-1.5 text-[11px] font-semibold tracking-wider text-slate-400 uppercase">{s.titulo}</p>
              <div className="space-y-0.5">
                {s.items.map((l) => (
                  <NavLink key={`${s.titulo}-${l.to}`} to={l.to} end data-testid={l.testid}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      }`
                    }>
                    <l.icon size={16} strokeWidth={1.75} /><span>{l.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100 shrink-0">
          <button data-testid="nav-perfil" onClick={() => { navigate("/perfil"); setSidebarOpen(false); }}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100">
            <UserCircle size={16} strokeWidth={1.75} />
            <span className="font-medium truncate">{user?.usuario}</span>
            {isAdmin && <span className="ml-auto text-[10px] bg-indigo-600 text-white px-1.5 py-0.5 rounded uppercase font-bold">admin</span>}
          </button>
        </div>
      </aside>

      <div className="md:pl-60">
        <header className="no-print sticky top-0 z-30 h-14 bg-white border-b border-slate-200 flex items-center gap-3 px-4">
          <button className="md:hidden text-slate-600" onClick={() => setSidebarOpen(true)} data-testid="sidebar-abrir-btn">
            <Menu size={20} />
          </button>
          <form onSubmit={buscarGlobal} className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="busqueda-global-input" value={busqueda}
              onChange={(e) => setBusqueda(busqCfg?.soloNumeros ? e.target.value.replace(/\D/g, "") : e.target.value)}
              placeholder={busqCfg?.placeholder || "Buscar en el catálogo..."}
              inputMode={busqCfg?.soloNumeros ? "numeric" : "text"}
              className="w-full h-9 pl-9 pr-3 rounded-md bg-slate-100 border border-transparent text-sm outline-none focus:bg-white focus:border-slate-300 transition-colors" />
          </form>
          <div className="ml-auto flex items-center gap-1">
            <Popover open={notifOpen} onOpenChange={(o) => { setNotifOpen(o); if (o) cargarNotifs(); }}>
              <PopoverTrigger asChild>
                <button data-testid="notificaciones-btn" title="Notificaciones"
                  className="relative w-9 h-9 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100">
                  <Bell size={17} strokeWidth={1.75} />
                  {noLeidas > 0 && (
                    <span data-testid="notificaciones-badge"
                      className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center">
                      {noLeidas}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 p-0">
                <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-800">Notificaciones</p>
                    <p className="text-[11px] text-indigo-600 font-semibold uppercase tracking-wide">
                      {noLeidas > 0 ? `${noLeidas} sin leer` : "Al día"}
                    </p>
                  </div>
                  {noLeidas > 0 && (
                    <button data-testid="notificaciones-marcar-todas" onClick={marcarTodas}
                      className="text-[11px] text-indigo-600 font-semibold hover:underline">
                      Marcar todas leídas
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifs.length === 0 && (
                    <p data-testid="notificaciones-vacio" className="p-5 text-sm text-slate-400 text-center">
                      No hay notificaciones
                    </p>
                  )}
                  {notifs.map((n) => (
                    <div key={n.id} data-testid={`notificacion-item-${n.id}`}
                      onClick={() => abrirNotif(n)}
                      className={`flex items-start gap-2 px-4 py-2.5 border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${n.leida ? "" : "bg-indigo-50/40"}`}>
                      {!n.leida && (
                        <span className="mt-1.5 w-2 h-2 rounded-full bg-indigo-500 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm text-slate-800 ${n.leida ? "font-medium" : "font-semibold"}`}>{n.titulo}</p>
                        <p className="text-xs text-slate-500">{n.detalle}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{tiempoRelativo(n.fecha_actualizacion)}</p>
                      </div>
                      <button data-testid={`notificacion-descartar-${n.id}`} title="Descartar"
                        onClick={(e) => descartar(e, n)}
                        className="mt-0.5 text-slate-400 hover:text-pink-600"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <div className="bg-primary text-white p-1.5 rounded-md hidden sm:block"><Bike size={16} /></div>
            <Button data-testid="logout-btn" variant="ghost" size="sm"
              className="text-slate-600 hover:bg-slate-100"
              onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut size={16} />
            </Button>
          </div>
        </header>
        <main className="max-w-none w-full px-6 py-6">{children}</main>
      </div>
    </div>
    </BusquedaCtx.Provider>
  );
}
